import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from '../lib/safeToast';
import {
  Plus,
  RefreshCw,
  Loader2,
  X,
  Trash2,
  User,
  Building2,
  Pencil,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getApiErrorMessage } from '../lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

// Country list with flags - includes AU, NZ, and others
const COUNTRIES = [
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'HK', name: 'Hong Kong', flag: '🇭🇰' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
];

const COUNTRIES_DEDUPED = COUNTRIES;

// Country-specific routing field config
const getRoutingConfig = (country) => {
  const c = country?.toUpperCase?.() || '';
  if (c === 'AU') return { label: 'BSB', key: 'bsb', placeholder: '000-000', maxLength: 6, required: true };
  if (c === 'GB') return { label: 'Sort Code', key: 'sort_code', placeholder: '00-00-00', maxLength: 8, required: false };
  if (c === 'US') return { label: 'Routing Number', key: 'routing_number', placeholder: '9 digits', maxLength: 9, required: false };
  if (c === 'IN') return { label: 'IFSC', key: 'ifsc', placeholder: '11 alphanumeric', maxLength: 11, required: false };
  if (['DE', 'FR', 'NL'].includes(c)) return { label: 'IBAN', key: 'iban', placeholder: 'Up to 34 chars', maxLength: 34, required: false };
  return { label: 'SWIFT/BIC', key: 'swift', placeholder: 'Optional for international', maxLength: 11, required: false };
};

const getCurrencyForCountry = (country) => {
  const map = { AU: 'AUD', NZ: 'NZD', US: 'USD', GB: 'GBP', SG: 'SGD', IN: 'INR', ID: 'IDR', PH: 'PHP', VN: 'VND', CN: 'CNY', HK: 'HKD', JP: 'JPY', DE: 'EUR', FR: 'EUR', NL: 'EUR' };
  return map[country] || 'AUD';
};

const getCurrencyCardClass = (country) => {
  const currency = getCurrencyForCountry(country);
  const classes = {
    AUD: 'bg-[#E6F7F3] border-[#A5E0D2]',
    NZD: 'bg-[#E6F7F3] border-[#A5E0D2]',
    USD: 'bg-blue-50 border-blue-200',
    INR: 'bg-amber-50 border-amber-200',
    IDR: 'bg-[#E6F7F3] border-[#A5E0D2]',
    PHP: 'bg-blue-50 border-blue-200',
    EUR: 'bg-purple-50 border-purple-200',
    GBP: 'bg-slate-50 border-slate-200',
    SGD: 'bg-red-50 border-red-200',
  };
  return classes[currency] || 'bg-slate-50 border-slate-200';
};

const Beneficiaries = () => {
  const { api } = useAuth();
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [bankCodes, setBankCodes] = useState([]);
  const [formData, setFormData] = useState({
    type: 'individual',
    full_name: '',
    company_name: '',
    email: '',
    country: 'AU',
    account_number: '',
    bank_name: '',
    bank_code: '',
    bsb: '',
    sort_code: '',
    routing_number: '',
    ifsc: '',
    iban: '',
    swift: '',
  });

  useEffect(() => {
    fetchBeneficiaries();
  }, []);

  useEffect(() => {
    const loadBankCodes = async () => {
      try {
        const res = await api.get('/gateway/bank-codes?country=ID');
        setBankCodes(res.data?.data || []);
      } catch { /* non-critical */ }
    };
    loadBankCodes();
  }, [api]);

  const fetchBeneficiaries = async () => {
    setLoading(true);
    try {
      const res = await api.get('/beneficiaries');
      setBeneficiaries(res.data?.data || []);
    } catch (error) {
      toast.error('Failed to fetch beneficiaries');
      setBeneficiaries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const { type, full_name, company_name, email, country, account_number, bank_name, bank_code, bsb, sort_code, routing_number, ifsc, iban, swift } = formData;
    if (!email?.trim()) { toast.error('Email is required'); return; }
    if (!account_number?.trim()) { toast.error('Account number is required'); return; }
    if (!bank_name?.trim()) { toast.error('Bank name is required'); return; }
    if (type === 'individual' && !full_name?.trim()) { toast.error('Full name is required for individual'); return; }
    if (type === 'business' && !company_name?.trim()) { toast.error('Company name is required for business'); return; }
    const routing = getRoutingConfig(country);
    if (routing.required && !formData[routing.key]?.trim()) {
      toast.error(`${routing.label} is required for ${COUNTRIES_DEDUPED.find(c => c.code === country)?.name || country}`);
      return;
    }

    setCreateLoading(true);
    try {
      const payload = {
        type,
        full_name: full_name?.trim() || null,
        company_name: company_name?.trim() || null,
        email: email.trim(),
        country,
        account_number: account_number.trim(),
        bank_name: bank_name.trim(),
        bank_code: bank_code?.trim() || null,
        bsb: bsb?.trim() || null,
        sort_code: sort_code?.trim() || null,
        routing_number: routing_number?.trim() || null,
        ifsc: ifsc?.trim() || null,
        iban: iban?.trim() || null,
        swift: swift?.trim() || null,
      };
      if (editingId) {
        await api.put(`/beneficiaries/${editingId}`, payload);
        toast.success('Beneficiary updated successfully');
        setEditingId(null);
      } else {
        await api.post('/beneficiaries', payload);
        toast.success('Beneficiary added successfully');
      }
      setShowCreate(false);
      resetForm();
      setSelectedBeneficiary(null);
      fetchBeneficiaries();
    } catch (error) {
      toast.error(getApiErrorMessage(error.response?.data?.detail, 'Failed to save beneficiary'));
    } finally {
      setCreateLoading(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      type: 'individual',
      full_name: '',
      company_name: '',
      email: '',
      country: 'AU',
      account_number: '',
      bank_name: '',
      bank_code: '',
      bsb: '',
      sort_code: '',
      routing_number: '',
      ifsc: '',
      iban: '',
      swift: '',
    });
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/beneficiaries/${id}`);
      toast.success('Beneficiary removed');
      setSelectedBeneficiary(null);
      fetchBeneficiaries();
    } catch (error) {
      toast.error(getApiErrorMessage(error.response?.data?.detail, 'Failed to delete'));
    }
  };

  const openEdit = (b) => {
    setFormData({
      type: b.type,
      full_name: b.full_name || '',
      company_name: b.company_name || '',
      email: b.email || '',
      country: b.country || 'AU',
      account_number: b.account_number || '',
      bank_name: b.bank_name || '',
      bank_code: b.bank_code || '',
      bsb: b.bsb || '',
      sort_code: b.sort_code || '',
      routing_number: b.routing_number || '',
      ifsc: b.ifsc || '',
      iban: b.iban || '',
      swift: b.swift || '',
    });
    setEditingId(b.id);
    setSelectedBeneficiary(null);
    setShowCreate(true);
  };

  const routingConfig = getRoutingConfig(formData.country);
  const displayName = (b) => b.type === 'business' ? (b.company_name || 'Business') : (b.full_name || 'Unknown');

  const getRoutingDisplays = (b) => {
    const items = [];
    if (b.bsb) items.push({ label: 'BSB', value: b.bsb });
    if (b.sort_code) items.push({ label: 'Sort Code', value: b.sort_code });
    if (b.routing_number) items.push({ label: 'Routing Number', value: b.routing_number });
    if (b.ifsc) items.push({ label: 'IFSC', value: b.ifsc });
    if (b.iban) items.push({ label: 'IBAN', value: b.iban });
    if (b.swift) items.push({ label: 'SWIFT/BIC', value: b.swift });
    return items;
  };

  return (
    <Layout>
      <div data-testid="beneficiaries-page" className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-extrabold text-[#0F172A] ">
              Beneficiaries
            </h1>
            <p className="text-slate-500 mt-2 text-base">Manage your saved recipients</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={fetchBeneficiaries} variant="outline" className="h-11 px-5 rounded-full">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={() => { setShowCreate(true); resetForm(); }}
              className="h-11 px-6 rounded-full bg-[#1C472B] hover:bg-[#143620] text-white"
              data-testid="add-beneficiary-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Beneficiary
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center h-48 items-center mt-8">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-[#1C472B]" />
          </div>
        ) : beneficiaries.length === 0 ? (
          <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm mt-8">
            <CardContent className="py-16 text-center">
              <div className="w-20 h-20 bg-[#E6F7F3] rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-10 h-10 text-[#1C472B]" />
              </div>
              <p className="text-lg font-medium text-slate-600 mb-2">No beneficiaries yet</p>
              <p className="text-slate-400 mb-6">Add recipients to quickly send payments later</p>
              <Button
                onClick={() => { setShowCreate(true); resetForm(); }}
                className="h-11 px-6 rounded-full bg-[#1C472B] hover:bg-[#143620] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Beneficiary
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {beneficiaries.map((b) => {
              const bankParts = [b.bank_name, b.account_number];
              if (b.bsb) bankParts.push(`BSB ${b.bsb}`);
              else if (b.ifsc) bankParts.push(`IFSC ${b.ifsc}`);
              else if (b.sort_code) bankParts.push(`Sort ${b.sort_code}`);
              else if (b.routing_number) bankParts.push(`Routing ${b.routing_number}`);
              const bankLine = bankParts.join(' · ');
              return (
              <Card
                key={b.id}
                onClick={() => setSelectedBeneficiary(b)}
                className={`rounded-2xl border-2 shadow-sm relative overflow-hidden cursor-pointer hover:shadow-md transition-all ${getCurrencyCardClass(b.country)}`}
                data-testid={`beneficiary-${b.id}`}
              >
                <div className="absolute top-4 right-4">
                  <span className="px-2 py-1 rounded-lg text-xs font-semibold bg-white/80 text-[#1C472B] border border-[#A5E0D2]/50">
                    {getCurrencyForCountry(b.country)}
                  </span>
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm border border-slate-100">
                      {b.type === 'business' ? (
                        <Building2 className="w-6 h-6 text-[#1C472B]" />
                      ) : (
                        <User className="w-6 h-6 text-[#1C472B]" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-[#0F172A]">{displayName(b)}</p>
                      <p className="text-xs text-slate-500 capitalize">{b.type}</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mt-3">{b.email}</p>
                  <p className="text-sm text-slate-600 mt-1 flex items-center gap-1">
                    <span>{COUNTRIES_DEDUPED.find(c => c.code === b.country)?.flag || '🌍'}</span>
                    {COUNTRIES_DEDUPED.find(c => c.code === b.country)?.name || b.country}
                  </p>
                  <div className="border-t border-slate-200 my-4" />
                  <p className="text-sm text-slate-600 font-medium">
                    {bankLine}
                  </p>
                </CardContent>
              </Card>
            );
            })}
          </div>
        )}

        {/* Beneficiary Details Modal - shown when clicking a card */}
        {selectedBeneficiary && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedBeneficiary(null)} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-visible" onClick={(e) => e.stopPropagation()}>
              <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#0F172A] ">Beneficiary Details</h2>
                <button onClick={() => setSelectedBeneficiary(null)} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <div className="px-6 py-6">
                <div className="flex flex-col items-center mb-6">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                    {selectedBeneficiary.type === 'business' ? (
                      <Building2 className="w-8 h-8 text-[#1C472B]" />
                    ) : (
                      <User className="w-8 h-8 text-[#1C472B]" />
                    )}
                  </div>
                  <p className="font-bold text-lg text-[#0F172A]">{displayName(selectedBeneficiary)}</p>
                  <p className="text-sm text-slate-500 capitalize">{selectedBeneficiary.type}</p>
                  <p className="text-sm text-slate-600 mt-1">{selectedBeneficiary.email}</p>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Country</span>
                    <span className="text-sm font-medium text-[#0F172A] flex items-center gap-1">
                      {COUNTRIES_DEDUPED.find(c => c.code === selectedBeneficiary.country)?.flag || '🌍'}
                      {COUNTRIES_DEDUPED.find(c => c.code === selectedBeneficiary.country)?.name || selectedBeneficiary.country}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Bank</span>
                    <span className="text-sm font-medium text-[#0F172A]">
                      {selectedBeneficiary.bank_name}
                      {selectedBeneficiary.bank_code && <span className="text-xs text-slate-400 ml-1">({selectedBeneficiary.bank_code})</span>}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Account Number</span>
                    <span className="text-sm font-mono font-medium text-[#0F172A]">{selectedBeneficiary.account_number}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Currency</span>
                    <span className="text-sm font-medium text-[#0F172A]">{getCurrencyForCountry(selectedBeneficiary.country)}</span>
                  </div>
                  {getRoutingDisplays(selectedBeneficiary).map((r) => (
                    <div key={r.label} className="flex justify-between items-center py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-500">{r.label}</span>
                      <span className="text-sm font-mono font-medium text-[#0F172A]">{r.value}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 mt-6">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 h-10 rounded-full text-sm border-red-200 text-[#F08080] hover:bg-red-50 hover:border-red-300 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove Beneficiary?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will remove {displayName(selectedBeneficiary)} from your saved recipients. You can add them again later.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => { handleDelete(selectedBeneficiary.id); setSelectedBeneficiary(null); }}
                          className="bg-[#F08080] hover:bg-[#E07070] text-white"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button
                    type="button"
                    onClick={() => openEdit(selectedBeneficiary)}
                    className="flex-1 h-10 rounded-full bg-[#1C472B] hover:bg-[#143620] text-white font-semibold text-sm"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Beneficiary Modal - compact layout, no scroll */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setShowCreate(false); resetForm(); }} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-visible">
              <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-[#0F172A] ">{editingId ? 'Edit Beneficiary' : 'Add New Beneficiary'}</h2>
                  <p className="text-slate-500 text-sm mt-0.5">{editingId ? 'Update beneficiary details' : 'Add a new recipient to your list'}</p>
                </div>
                <button
                  onClick={() => { setShowCreate(false); resetForm(); }}
                  className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              <form onSubmit={handleCreate} className="px-6 py-4">
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  {/* Type */}
                  <div className="col-span-2">
                    <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Beneficiary Type</Label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, type: 'individual' })}
                        className={`flex-1 flex items-center gap-2 py-2.5 px-3 rounded-lg border-2 transition-colors ${
                          formData.type === 'individual' ? 'border-[#1C472B] bg-[#E6F7F3]' : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <User className="w-4 h-4 text-slate-600" />
                        <span className="text-sm font-semibold text-[#0F172A]">Individual</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, type: 'business' })}
                        className={`flex-1 flex items-center gap-2 py-2.5 px-3 rounded-lg border-2 transition-colors ${
                          formData.type === 'business' ? 'border-[#1C472B] bg-[#E6F7F3]' : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Building2 className="w-4 h-4 text-slate-600" />
                        <span className="text-sm font-semibold text-[#0F172A]">Business</span>
                      </button>
                    </div>
                  </div>

                  {formData.type === 'individual' && (
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 mb-1 block">Full Name</Label>
                      <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} placeholder="John Doe" className="h-9 rounded-lg border-slate-200 text-sm" required />
                    </div>
                  )}
                  {formData.type === 'business' && (
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 mb-1 block">Company Name</Label>
                      <Input value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} placeholder="ACME Pty Ltd" className="h-9 rounded-lg border-slate-200 text-sm" required />
                    </div>
                  )}

                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Email</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john@example.com" className="h-9 rounded-lg border-slate-200 text-sm" required />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Country</Label>
                    <Select value={formData.country} onValueChange={(v) => setFormData({ ...formData, country: v })}>
                      <SelectTrigger className="h-9 rounded-lg border-slate-200 text-sm">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES_DEDUPED.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            <span className="flex items-center gap-2"><span>{c.flag}</span>{c.name}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Account Number</Label>
                    <Input value={formData.account_number} onChange={(e) => setFormData({ ...formData, account_number: e.target.value })} placeholder="1234567890" className="h-9 rounded-lg border-slate-200 font-mono text-sm" required />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Bank Name</Label>
                    {formData.country === 'ID' && bankCodes.length > 0 ? (
                      <Select value={formData.bank_code || '__other__'} onValueChange={(v) => {
                        if (v === '__other__') {
                          setFormData({ ...formData, bank_code: '', bank_name: '' });
                        } else {
                          const bank = bankCodes.find((b) => b.code === v);
                          setFormData({ ...formData, bank_code: v, bank_name: bank?.name || v });
                        }
                      }}>
                        <SelectTrigger className="h-9 rounded-lg border-slate-200 text-sm">
                          <SelectValue placeholder="Select bank" />
                        </SelectTrigger>
                        <SelectContent>
                          {bankCodes.map((b) => (
                            <SelectItem key={b.code} value={b.code}>{b.name} ({b.code})</SelectItem>
                          ))}
                          <SelectItem value="__other__">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input value={formData.bank_name} onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} placeholder="Bank Name" className="h-9 rounded-lg border-slate-200 text-sm" required />
                    )}
                  </div>

                  {(formData.country === 'AU' || formData.country === 'GB' || formData.country === 'US' || formData.country === 'IN' || ['DE', 'FR', 'NL'].includes(formData.country)) && (
                    <div className="col-span-2">
                      <Label className="text-xs font-semibold text-slate-600 mb-1 block">
                        {routingConfig.label}{routingConfig.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <Input
                        value={formData[routingConfig.key] || ''}
                        onChange={(e) => {
                          const val = ['bsb', 'sort_code', 'routing_number'].includes(routingConfig.key)
                            ? e.target.value.replace(/\D/g, '').slice(0, routingConfig.maxLength)
                            : e.target.value.slice(0, routingConfig.maxLength);
                          setFormData({ ...formData, [routingConfig.key]: val });
                        }}
                        placeholder={routingConfig.placeholder}
                        maxLength={routingConfig.maxLength}
                        className="h-9 rounded-lg border-slate-200 font-mono text-sm"
                        required={routingConfig.required}
                      />
                    </div>
                  )}

                  <div className="col-span-2 flex gap-3 pt-3">
                    <Button type="button" variant="outline" onClick={() => { setShowCreate(false); resetForm(); }} className="flex-1 h-10 rounded-full text-sm border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900">
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 h-10 rounded-full bg-[#1C472B] hover:bg-[#143620] text-white font-semibold text-sm" disabled={createLoading}>
                      {createLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? 'Save Changes' : 'Add Beneficiary')}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Beneficiaries;
